import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Lock, User } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useAuthActions, useIsAuthenticated } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type LoginForm = z.infer<typeof schema>;

const EASE = [0.22, 1, 0.36, 1] as const;

export function LoginPage() {
  const isAuthenticated = useIsAuthenticated();
  const { login } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const reduce = useReducedMotion();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(schema) });

  if (isAuthenticated) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? ROUTES.dashboard;
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    setSubmitting(true);
    try {
      await login(data);
      toast.success('Welcome back!');
      const from = (location.state as { from?: Location })?.from?.pathname ?? ROUTES.dashboard;
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Login failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.1, delayChildren: reduce ? 0 : 0.15 } },
  };
  const item: Variants = {
    hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f8f7ff] p-4">
      {/* Aurora background */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute -left-32 -top-32 h-[34rem] w-[34rem] rounded-full bg-violet-300/55 blur-[140px]"
          animate={reduce ? undefined : { x: [0, 40, 0], y: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-40 -right-24 h-[32rem] w-[32rem] rounded-full bg-indigo-200/70 blur-[140px]"
          animate={reduce ? undefined : { x: [0, -50, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute left-1/2 top-20 h-80 w-[34rem] -translate-x-1/2 rounded-full bg-violet-200/65 blur-[110px]"
          animate={reduce ? undefined : { opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 10, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        />
        {/* faint grid */}
        <div
          className="absolute inset-0 opacity-[0.42]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #dcd7f4 1px, transparent 1px), linear-gradient(to bottom, #dcd7f4 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 flex w-full max-w-sm flex-col items-center"
      >
        {/* Brand */}
        <motion.div
          variants={item}
          className="mb-7 flex flex-col items-center gap-4"
        >
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div className="relative h-28 w-72 overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-br from-[#171134] via-[#251652] to-[#121b42] shadow-[0_18px_45px_rgba(84,52,180,0.25)]" aria-label="ZYNX CRM">
              <div className="absolute inset-x-8 top-4 h-20 rounded-full bg-violet-500/45 blur-3xl" aria-hidden />
              <img src="/zynx-logo.png" alt="ZYNX CRM" className="absolute left-0 top-[-92px] w-72 max-w-none mix-blend-screen brightness-125 contrast-125 drop-shadow-[0_12px_28px_rgba(124,58,237,0.45)]" />
            </div>
          </motion.div>
          <p className="text-body-sm font-semibold tracking-wide text-violet-950/70">Enterprise CRM Workspace</p>
        </motion.div>

        {/* Card */}
        <motion.div
          variants={item}
          className="w-full rounded-card border border-violet-100 bg-white/95 p-7 shadow-[0_24px_60px_rgba(55,36,125,0.15)] backdrop-blur-xl"
        >
          <div className="mb-5">
            <h1 className="text-headline-md font-semibold text-on-surface">Sign in</h1>
            <p className="mt-0.5 text-body-sm text-on-surface-variant">Welcome back — access your workspace.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <motion.div variants={item}>
              <Input
                label="Username"
                placeholder="you@rbmoilcorporation.in"
                autoComplete="username"
                leftIcon={<User className="h-4 w-4" />}
                error={errors.username?.message}
                {...register('username')}
              />
            </motion.div>
            <motion.div variants={item}>
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                leftIcon={<Lock className="h-4 w-4" />}
                error={errors.password?.message}
                {...register('password')}
              />
            </motion.div>
            <motion.div variants={item}>
              <Button type="submit" className="w-full" loading={submitting}>
                Sign in
              </Button>
            </motion.div>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.p variants={item} className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-violet-950/45">
          ZYNX · by RBM Oil Corporation
        </motion.p>
      </motion.div>
    </div>
  );
}
